import Head from "next/head";
import Link from "next/link";

import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { openPdfInNewTab } from "src/utils/utils";
import { global_config } from "src/shared/global_config";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";
import type { TRegistrationCardData } from "src/shared/types/globalStotre.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    group: "Games/Months Included",
    question: "What games are included with the Kings Pass?",
    answer: (
      <>
        The Kings Pass includes a minimum of 34 games during the season. Select
        games are excluded (see below). The number of games varies by month with
        4-8 games each month (October-March). See monthly calendar on{" "}
        <Link href={"https://www.nba.com/kings/pass"} target="_blank">
          Kings.com/Pass
        </Link>{" "}
        for games included in the current month that&apos;s on sale. Excluded
        games: Lakers, Warriors, Spurs, 76ers, Knicks and 4/9 Mavericks.
      </>
    ),
  },
  {
    group: "Games/Months Included",
    question: "Does my pass automatically renew each month?",
    answer:
      "The monthly Kings Pass will automatically renew for the next month through March 2027. There is an opt out window each month if you do not want your pass to renew. If you purchase your pass at the season price, you will automatically be charged each month through March 2027. There is no opt out window with the season pass.",
  },
  {
    group: "Games/Months Included",
    question:
      "How do I opt out of next month if I don't want to continue my Kings Pass (monthly pass only)?",
    answer:
      "You must opt out by the 25th of the prior month if you want to discontinue your Kings Pass. For example, if you want to discontinue your pass for November, you need to opt out by October 25th. To opt out, log in to your Kings Pass account and select “Manage Subscription.” Annual Pass subscription members are not eligible to opt out of an upcoming month.",
  },
  {
    group: "Pass/Seat Locations",
    question: "Where can I expect to sit each game?",
    answer:
      "If you have the Kings Pass Basic, you are guaranteed a seat in the upper level. If you have the Kings Pass Preferred, you are guaranteed a seat in rows B - E in the upper level. For both Basic and Preferred, your specific seat location will likely vary game to game as seats will be randomly assigned to pass buyers on the day of the game.",
  },
  {
    group: "Pass/Seat Locations",
    question: "How do I get my seat location(s) and ticket(s)?",
    answer:
      "You will receive a text message the day before each game asking if you will be attending the next game. You will need to log in to your Kings Pass account to check in for each game. If you indicate that you're attending that game, you will receive a text message a few hours before the game with a link to your tickets with your seat location.",
  },
  {
    group: "Pass/Seat Locations",
    question: "What if I lose the text message with my pass?",
    answer: (
      <>
        You can access your tickets by going to{" "}
        <Link href={"https://www.kings.com/ClickTix"} target="_blank">
          Kings.com/ClickTix
        </Link>{" "}
        and logging into your account.
      </>
    ),
  },
  {
    group: "Pass/Seat Locations",
    question:
      "If I buy two or more passes, will my seats be next to each other?",
    answer:
      "Yes, passes purchased together will receive their seats next to each other as long as they are the same type of Kings Pass (i.e. all must be Kings Pass Basic or all must be Kings Pass Preferred).",
  },
  {
    group: "Pass/Seat Locations",
    question:
      "Can my seat be next to a friend who also purchased the Kings Pass?",
    answer: (
      <>
        Yes, if your friend purchased the same type of Kings Pass as you (both
        purchased Basic or both purchased Preferred), you will have the
        opportunity to link your pass to another pass buyer in order to sit next
        to each other. You can do this by logging into your Kings Pass Account,
        clicking &quot;
        <Link
          href={{
            pathname: routes.my_group,
            query: { teamSchema: "sacramentokings_pass" },
          }}
        >
          My Group
        </Link>
        &quot; in the menu, and generating a unique link to share with friends
        to sit with you this season.
      </>
    ),
  },
  {
    group: "Other",
    question: "How many passes can I buy?",
    answer: "You can buy up to four passes per account.",
  },
  {
    group: "Other",
    question: "Can I get a refund for unused tickets?",
    answer: "Passes are non-transferable and non-refundable.",
  },
  {
    group: "Other",
    question: "Can I sell my pass for games that I do not attend?",
    answer: "No, passes are non-transferable.",
  },
  {
    group: "Other",
    question: "Can I share my pass with someone else?",
    answer: "Passes cannot be shared and are non-transferable.",
  },
  {
    group: "Other",
    question: "What do I need to access my digital tickets for the game?",
    answer:
      "You will need a smartphone with a web browser to access and display your digital tickets for admission to Golden 1 Center.",
  },
];

export const TERMS_AND_CONDITION = () => {
  const theme = useSSTheme();
  return (
    <>
      By purchasing you understand that you are agreeing to a renewal plan for
      the 2026-27 season. You will be charged 6 installments of $99, one today
      and with 5 payments made during the rest of the season. Please note with
      this renewal offer, you are committing to the season-long Kings Pass for
      the 2026-27 season. You will not be able to cancel or request a refund
      once you have renewed your Kings Pass. Tickets are non-transferable and
      not eligible for resale. You also agree to receive SMS alerts regarding
      ticket delivery and agree to the{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(global_config.TERMS_AND_CONDITIONS_PDF_URL);
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Terms and Conditions
      </span>{" "}
      and{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(global_config.PRIVACY_POLICY_PDF_URL);
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Privacy Policy
      </span>
      .
    </>
  );
};

export const TERMS_AND_CONDITION_OPT_OUT_RESTRICTED = () => {
  const theme = useSSTheme();
  return (
    <>
      By purchasing you understand that you are agreeing to a renewal plan for
      the 2026-27 season. You will be charged 6 installments of $99, one today
      and with 5 payments made during the rest of the season. Please note with
      this renewal offer, you are committing to the season-long Kings Pass for
      the 2026-27 season. You will not be able to cancel or request a refund
      once you have renewed your Kings Pass. Tickets are non-transferable and
      not eligible for resale. You also agree to receive SMS alerts regarding
      ticket delivery and agree to the{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(global_config.TERMS_AND_CONDITIONS_PDF_URL);
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Terms and Conditions
      </span>{" "}
      and{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(global_config.PRIVACY_POLICY_PDF_URL);
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Privacy Policy
      </span>
      .
    </>
  );
};

export const TERMS_AND_CONDITION_ADD_ON = () => {
  const theme = useSSTheme();
  return (
    <>
      By purchasing you agree to receive SMS alerts regarding ticket delivery
      and agree to the{" "}
      <Link
        target="_blank"
        href="https://www.attend.tech/terms-of-use"
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Terms of Use
      </Link>{" "}
      and{" "}
      <Link
        target="_blank"
        href="https://www.attend.tech/privacy-policy"
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Privacy Policy
      </Link>
      .
    </>
  );
};

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      <script
        defer
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1846888515544840');
              fbq('track', 'PageView');
              window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
                const { cartRes } = e.detail;
                fbq('track', 'Purchase', {
                  value: cartRes.totalAmount,
                  currency: 'USD',
                });
                console.log('meta pixel purchase event fired.');
              });
            `,
        }}
      />
    </Head>
  );
};

export const PRE_PURCHASE_FAQS_BY_PASS_ID: TTeamConfig["LANGUAGES"]["PRE_PURCHASE_FAQS_BY_PASS_ID"] =
  {
    1: FAQS,
    2: FAQS,
    3: FAQS,
    4: FAQS,
  };

export const TERMS_AND_CONDITIONS_BY_PASS_ID: TTeamConfig["LANGUAGES"]["TERMS_AND_CONDITIONS_BY_PASS_ID"] =
  {
    // Preferred Season Pass
    1: () => (
      <>
        By checking this box, you acknowledge and agree to be charged each month
        through March, with no opt-out option. Your pass will automatically
        renew each month during this period, without cancellation. You also
        agree to the{" "}
        <Link target="_blank" href="https://www.attend.tech/terms-of-use">
          Terms of Use
        </Link>{" "}
        and{" "}
        <Link target="_blank" href="https://www.attend.tech/privacy-policy">
          Privacy Policy
        </Link>
        .
      </>
    ),

    // Preferred Monthly Pass
    2: () => (
      <>
        By checking this box, you acknowledge and agree that your pass will
        automatically renew each month unless you opt out during the designated
        opt-out window. You also agree to the{" "}
        <Link target="_blank" href="https://www.attend.tech/terms-of-use">
          Terms of Use
        </Link>{" "}
        and{" "}
        <Link target="_blank" href="https://www.attend.tech/privacy-policy">
          Privacy Policy
        </Link>
        .
      </>
    ),

    // Basic Season Pass
    3: () => (
      <>
        By checking this box, you acknowledge and agree to be charged each month
        through March, with no opt-out option. Your pass will automatically
        renew each month during this period, without cancellation. You also
        agree to the{" "}
        <Link target="_blank" href="https://www.attend.tech/terms-of-use">
          Terms of Use
        </Link>{" "}
        and{" "}
        <Link target="_blank" href="https://www.attend.tech/privacy-policy">
          Privacy Policy
        </Link>
        .
      </>
    ),

    // Basic Monthly Pass
    4: () => (
      <>
        By checking this box, you acknowledge and agree that your pass will
        automatically renew each month unless you opt out during the designated
        opt-out window. You also agree to the{" "}
        <Link target="_blank" href="https://www.attend.tech/terms-of-use">
          Terms of Use
        </Link>{" "}
        and{" "}
        <Link target="_blank" href="https://www.attend.tech/privacy-policy">
          Privacy Policy
        </Link>
        .
      </>
    ),

    // gift passes
    38: () => (
      <>
        By checking this box, you acknowledge and agree to a one-time charge for
        this pass. You also agree to the{" "}
        <Link target="_blank" href="https://www.attend.tech/terms-of-use">
          Terms of Use
        </Link>{" "}
        and{" "}
        <Link target="_blank" href="https://www.attend.tech/privacy-policy">
          Privacy Policy
        </Link>
        .
      </>
    ),
    39: () => (
      <>
        By checking this box, you acknowledge and agree to a one-time charge for
        this pass. You also agree to the{" "}
        <Link target="_blank" href="https://www.attend.tech/terms-of-use">
          Terms of Use
        </Link>{" "}
        and{" "}
        <Link target="_blank" href="https://www.attend.tech/privacy-policy">
          Privacy Policy
        </Link>
        .
      </>
    ),
  };

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
  {
    TITLE: () => <>You&apos;re all set to experience the action!</>,
    DESCRIPTION: () => {
      const router = useRouterExt();
      return (
        <>
          Invite your friends! Create groups{" "}
          <b>
            <u>
              <span
                onClick={() => router.pushSchema(routes.my_group)}
                style={{ cursor: "pointer" }}
              >
                here
              </span>
            </u>
          </b>
          .
        </>
      );
    },
  };

export const UPGRADES_CARDS: TRegistrationCardData[] = [
  {
    title: "Kings Pass: Exclusive Upgrade Offer",
    description:
      "For a limited time, upgrade your Kings Pass Basic to Kings Pass Preferred with guaranteed seats in rows B-E in the upper level! Pay $50 per pass today to start by upgrading your October Pass; subsequent months will auto-renew at $149/month through March for the preferred seating option.",
    buttonLabel: "View Upgrades",
    authRedirectPage: routes.home,
  },
];
